import React, { useState } from 'react';
import { useArchivedPrograms } from '../hooks/usePrograms';
import { useLocalStorage } from '../hooks/useLocalStorage';
import ProgramDetailModal from '../components/programs/ProgramDetailModal';
import SearchBar from '../components/common/SearchBar';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Pagination from '../components/common/Pagination';
import { Program } from '../services/api';
import {
  ArchiveBoxIcon,
  Squares2X2Icon,
  SquaresPlusIcon,
  ListBulletIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

type ViewMode = 'card' | 'gallery' | 'list';

const Archives: React.FC = () => {
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedProgramForDetail, setSelectedProgramForDetail] = useState<Program | null>(null);
  const [searchQuery, setSearchQuery] = useLocalStorage<string>('archives-search-query', '');
  const [viewMode, setViewMode] = useLocalStorage<ViewMode>('archives-view-mode', 'list');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(20);

  const { data: archivedPrograms, isLoading } = useArchivedPrograms();

  const filteredPrograms = archivedPrograms?.filter(program => {
    const matchesSearch = !searchQuery ||
      program.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      program.affiliateUrl.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesSearch;
  }) || [];

  // Pagination logic
  const totalItems = filteredPrograms.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPrograms = filteredPrograms.slice(startIndex, endIndex);

  // Reset to first page when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, setCurrentPage]);

  const handleViewDetails = (program: Program) => {
    setSelectedProgramForDetail(program);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedProgramForDetail(null);
  };

  const formatDate = (date?: Date | string) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const renderViewModeButtons = () => (
    <div className="flex items-center space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
      <button
        onClick={() => setViewMode('card')}
        className={`p-2 rounded-md transition-colors ${
          viewMode === 'card'
            ? 'bg-white dark:bg-gray-600 text-primary-600 dark:text-primary-400 shadow-sm'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
        }`}
        title="Card View"
      >
        <Squares2X2Icon className="h-5 w-5" />
      </button>
      <button
        onClick={() => setViewMode('gallery')}
        className={`p-2 rounded-md transition-colors ${
          viewMode === 'gallery'
            ? 'bg-white dark:bg-gray-600 text-primary-600 dark:text-primary-400 shadow-sm'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
        }`}
        title="Gallery View"
      >
        <SquaresPlusIcon className="h-5 w-5" />
      </button>
      <button
        onClick={() => setViewMode('list')}
        className={`p-2 rounded-md transition-colors ${
          viewMode === 'list'
            ? 'bg-white dark:bg-gray-600 text-primary-600 dark:text-primary-400 shadow-sm'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
        }`}
        title="List View"
      >
        <ListBulletIcon className="h-5 w-5" />
      </button>
    </div>
  );

  const renderCardView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {paginatedPrograms.map((program) => (
        <div
          key={program._id}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4 hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => handleViewDetails(program)}
        >
          <div className="flex items-start space-x-3">
            <img
              src={program.logo}
              alt={program.name}
              className="w-16 h-16 object-contain rounded"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-gray-900 dark:text-white truncate">
                {program.name}
              </h3>
              <div className="mt-1 flex items-center text-xs text-gray-500 dark:text-gray-400">
                <CalendarIcon className="h-3 w-3 mr-1" />
                <span>Archived: {formatDate(program.archivedAt)}</span>
              </div>
              <div className="mt-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                  Archived
                </span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderGalleryView = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
      {paginatedPrograms.map((program) => (
        <div
          key={program._id}
          className="rounded-lg shadow-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => handleViewDetails(program)}
        >
          <div className="aspect-square bg-gray-100 dark:bg-gray-700 flex items-center justify-center p-4 relative">
            <img
              src={program.logo}
              alt={program.name}
              className="max-w-full max-h-full object-contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                target.nextElementSibling?.classList.remove('hidden');
              }}
            />
            <div className="hidden text-2xl font-bold text-gray-400 dark:text-gray-500">
              {program.name.charAt(0)}
            </div>
            <div className="absolute top-2 right-2">
              <div className="bg-gray-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                <ArchiveBoxIcon className="h-3 w-3" />
              </div>
            </div>
          </div>
          <div className="p-3">
            <h3 className="font-medium text-gray-900 dark:text-white text-sm truncate">
              {program.name}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              Archived {formatDate(program.archivedAt)}
            </p>
          </div>
        </div>
      ))}
    </div>
  );

  const renderListView = () => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Program
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Cashback Rate
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Archived Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {paginatedPrograms.map((program) => (
              <tr
                key={program._id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                onClick={() => handleViewDetails(program)}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <img
                        className="h-10 w-10 rounded-full object-cover"
                        src={program.logo}
                        alt={program.name}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                      <div className="hidden h-10 w-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-sm font-medium text-gray-700 dark:text-gray-300">
                        {program.name.charAt(0)}
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {program.name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                        {program.affiliateUrl}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                    <ArchiveBoxIcon className="h-3 w-3 mr-1" />
                    Archived
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {program.cashbackRate ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                      {program.cashbackRate}%
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                      Not set
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center">
                    <CalendarIcon className="h-4 w-4 mr-1" />
                    {formatDate(program.archivedAt)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDetails(program);
                    }}
                    className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300"
                  >
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (viewMode) {
      case 'card':
        return renderCardView();
      case 'gallery':
        return renderGalleryView();
      case 'list':
        return renderListView();
      default:
        return renderListView();
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
            <ArchiveBoxIcon className="h-7 w-7 mr-2" />
            Archived Programs
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Programs that are no longer in 'joined' status on AWIN
          </p>
        </div>
      </div>

      {/* Stats Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Total Archived Programs</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {archivedPrograms?.length || 0}
            </p>
          </div>
          <div className="bg-gray-100 dark:bg-gray-700 rounded-full p-3">
            <ArchiveBoxIcon className="h-8 w-8 text-gray-600 dark:text-gray-400" />
          </div>
        </div>
      </div>

      {/* Search and View Mode */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0 lg:space-x-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <SearchBar
              onSearch={setSearchQuery}
              initialValue={searchQuery}
              placeholder="Search archived programs..."
            />
          </div>

          {/* View Mode Toggle */}
          {renderViewModeButtons()}
        </div>
      </div>

      {/* Archives Content */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      ) : filteredPrograms.length > 0 ? (
        <div className="space-y-4">
          {renderContent()}

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
          />
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center border border-gray-200 dark:border-gray-700">
          <div className="text-gray-400 dark:text-gray-500 mb-3">
            <ArchiveBoxIcon className="h-12 w-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No archived programs found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {searchQuery
              ? 'Try adjusting your search'
              : 'Programs that are no longer in joined status will appear here'
            }
          </p>
        </div>
      )}

      {/* Program Detail Modal */}
      <ProgramDetailModal
        program={selectedProgramForDetail}
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        onEdit={() => {}} // Disabled for archived programs
        onToggleVisibility={() => {}} // Disabled for archived programs
      />
    </div>
  );
};

export default Archives;
